import React, { FC } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { postSurveysCampaignByIdNextCall } from '@/surveys/state/api/postSurveysCampaignByIdNextCall';
import { QuestionType } from '@/swagger/constants/surveys-enums';
import { DynamicSurveySkeleton } from './DynamicSurveySkeleton';
import { MultipleChoiceAnswer } from './MultipleChoiceAnswer';
import { RangeChoiceAnswer } from './RangeChoiceAnswer';
import { SingleChoiceAnswer } from './SingleChoiceAnswer';

type Props = {
  currentStep: ReturnType<(typeof postSurveysCampaignByIdNextCall)['hook']>['activeStep'] & {
    stepNumber?: number;
  };
  control: Control<FieldValues>;
};

export const DynamicSurvey: FC<Props> = ({ currentStep, control }) => {
  const t = useTranslations('configuration.learning.employeeCompleteSurvey');
  const { loading: loadingPostSurveysCampaignByIdNext } = postSurveysCampaignByIdNextCall.hook();

  return loadingPostSurveysCampaignByIdNext ? (
    <DynamicSurveySkeleton />
  ) : (
    <Box>
      {currentStep?.totalSteps && (
        <Typography variant='p3/light' mb='xxs'>
          {t('stepXonY', {
            currentStep: (currentStep?.stepNumber || 0) + 1,
            totalSteps: currentStep.totalSteps,
          })}
        </Typography>
      )}
      <Typography variant='h4' mb='s'>
        {currentStep.nextQuestion?.text}
      </Typography>
      {/*
       * Use a key tied to questionId to force a remount when the question changes.
       * This prevents React from reusing the previous component instance (and any
       * react-hook-form field registration/state), avoiding stale values carrying
       * over from the previous question.
       */}
      {currentStep.nextQuestion?.type === QuestionType.SINGLE_CHOICE && (
        <SingleChoiceAnswer
          key={`single-${currentStep.nextQuestion?.id}`}
          currentStep={currentStep}
          control={control}
        />
      )}
      {currentStep.nextQuestion?.type === QuestionType.MULTIPLE_CHOICE && (
        <MultipleChoiceAnswer
          key={`multi-${currentStep.nextQuestion?.id}`}
          currentStep={currentStep}
          control={control}
        />
      )}
      {currentStep.nextQuestion?.type === QuestionType.DIFFERENTIAL && (
        <RangeChoiceAnswer
          key={`range-${currentStep.nextQuestion?.id}`}
          currentStep={currentStep}
          control={control}
        />
      )}
    </Box>
  );
};
